import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slide,
  Backdrop,
  Zoom,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ErrorPopupProps {
  open: boolean;
  errorTitle?: string;
  errorMessage?: string;
  onClose: () => void;
}

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ErrorPopup: React.FC<ErrorPopupProps> = ({
  open,
  errorTitle,
  errorMessage,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="error-dialog-title"
      aria-describedby="error-dialog-description"
      TransitionComponent={SlideTransition}
      TransitionProps={{ timeout: 400 }}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 400,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(8px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
          overflow: "visible",
          maxWidth: { xs: "90vw", sm: "440px" },
          width: { xs: "90vw", sm: "440px" },
          margin: { xs: "16px", sm: "32px" },
          background: "linear-gradient(145deg, #ffffff 0%, #fafafa 100%)",
          position: "relative",
        },
      }}
      sx={{
        zIndex: 9999,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box>
        <DialogTitle
          id="error-dialog-title"
          sx={{
            padding: { xs: "20px 14px 12px", sm: "24px 16px 16px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <Zoom in={open} timeout={600} style={{ transitionDelay: "200ms" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(244, 67, 54, 0.25)",
                position: "relative",
                // animation: "shake 0.6s ease-in-out 0.3s both",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: -8,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(239, 83, 80, 0.1))",
                  // animation: "rotate 4s linear infinite",
                },
                // "@keyframes shake": {
                //   "0%, 100%": {
                //     transform: "translateX(0) rotate(0deg)",
                //   },
                //   "10%, 30%, 50%, 70%, 90%": {
                //     transform: "translateX(-5px) rotate(-5deg)",
                //   },
                //   "20%, 40%, 60%, 80%": {
                //     transform: "translateX(5px) rotate(5deg)",
                //   },
                // },
                // "@keyframes rotate": {
                //   "0%": {
                //     transform: "rotate(0deg)",
                //   },
                //   "100%": {
                //     transform: "rotate(360deg)",
                //   },
                // },
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 24,
                  color: "#f44336",
                  filter: "drop-shadow(0 2px 8px rgba(244, 67, 54, 0.4))",
                  // animation: "errorPulse 2s ease-in-out infinite",
                  // "@keyframes errorPulse": {
                  //   "0%, 100%": {
                  //     transform: "scale(1)",
                  //     opacity: 1,
                  //   },
                  //   "50%": {
                  //     transform: "scale(1.15)",
                  //     opacity: 0.9,
                  //   },
                  // },
                }}
              />
            </Box>
          </Zoom>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #c62828 0%, #d32f2f 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1rem", sm: "1.2rem" },
              letterSpacing: "-0.02em",
              // animation: "fadeInUp 0.5s ease-out 0.3s both",
              // "@keyframes fadeInUp": {
              //   from: {
              //     opacity: 0,
              //     transform: "translateY(10px)",
              //   },
              //   to: {
              //     opacity: 1,
              //     transform: "translateY(0)",
              //   },
              // },
            }}
          >
            {errorTitle || "Error!"}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: { xs: "0 14px 24px", sm: "0 16px 24px" },
            textAlign: "center",
            minHeight: "auto",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              color: "#424242",
              lineHeight: 1.7,
              fontWeight: 400,
              maxWidth: "100%",
              wordBreak: "break-word",
              // animation: "fadeInUp 0.5s ease-out 0.4s both",
            }}
          >
            {errorMessage || "An unexpected error occurred. Please try again."}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "0 14px 20px", sm: "0 16px 24px" },
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
              color: "#ffffff",
              padding: { xs: "10px 20px", sm: "12px 24px" },
              fontWeight: 600,
              borderRadius: "8px",
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              textTransform: "none",
              minWidth: { xs: "120px", sm: "140px" },
              height: { xs: "40px", sm: "44px" },
              boxShadow: "0 8px 24px rgba(244, 67, 54, 0.35)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              // animation: "fadeInUp 0.5s ease-out 0.5s both",
              // "&::before": {
              //   content: '""',
              //   position: "absolute",
              //   top: 0,
              //   left: "-100%",
              //   width: "100%",
              //   height: "100%",
              //   background:
              //     "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
              //   transition: "left 0.5s",
              // },
              "&:hover": {
                background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: "0 12px 32px rgba(244, 67, 54, 0.45)",
                "&::before": {
                  left: "100%",
                },
              },
              "&:active": {
                transform: "translateY(-1px) scale(0.98)",
                boxShadow: "0 4px 16px rgba(244, 67, 54, 0.35)",
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ErrorPopup;
